import { ActivityStatus, RegistrationStatus } from '@fieldday/fielddayportal-model'
import axios from 'axios'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ScheduledActivityMessage } from '../models/ScheduledActivityMessage'
import { useAPI } from '../util/useAPI'
import { useNonprofitContext } from './commonOrgContext'
import { useTeamRoleContext } from './teamRoleContext'
import { useAuth } from './useAuth'
import useInterval, { intervalDelay } from './useInterval'
import { AlertSeverity, useLoading } from './useLoading'
import { useScheduledActivityContext } from './useScheduledActivityContext'

type ContextProps = {
  children: React.ReactNode
}

export const defaultDelay = 5

type MessagesForContext = {
  scheduledActivityMessages: ScheduledActivityMessage[] | null
  scheduledActivityTeamMessages: ScheduledActivityMessage[] | null
  createScheduledActivityMessage: (messageBody: string, parentMessageId?: string, orgUnitId?: string | null) => Promise<boolean>
  pauseMessagesSubscription: () => void
  resumeMessagesSubscription: () => void
  messagesLoaded: boolean
  canSeeMessages: boolean
}

const ScheduledActivityMessagesContext = createContext<MessagesForContext>(
  { scheduledActivityMessages: {}} as MessagesForContext
)

export function ScheduledActivityMessagesContextProvider({ children }: ContextProps) {
  const scheduledActivityMessages = useProvideScheduledActivityMessages()

  return (
    <ScheduledActivityMessagesContext.Provider value={scheduledActivityMessages}>
      {children}
    </ScheduledActivityMessagesContext.Provider>
  )
}

export const useScheduledActivityMessagesContext = () => {
  return useContext(ScheduledActivityMessagesContext)
}

export function useProvideScheduledActivityMessages(): MessagesForContext {
  const FieldDayAPI = useAPI()
  const { user } = useAuth()
  const { setAlert } = useLoading()
  const { canCoordinate: isNpoCoordinator } = useNonprofitContext()
  const { isMember: isTeamMember } = useTeamRoleContext()
  const { scheduledActivity, registrations, guestRegistrations, ouIdForTeamMessagesAndPhotos: ouIdForTeamMessages } = useScheduledActivityContext()
  const scheduledActivityId = scheduledActivity.id

  const messageAbortController = useRef(new AbortController)

  const [scheduledActivityMessages, setMessages] = useState<ScheduledActivityMessage[] | null>(null)
  const [scheduledActivityTeamMessages, setTeamMessages] = useState<ScheduledActivityMessage[] | null>(null)
  const [messagesETag, setMessageETag] = useState("")

  const [refreshInterval, setRefreshInterval] = useState(null as intervalDelay)
  const [timeSinceNewMessage, setTimeSinceNewMessage] = useState(0)

  const myReg = [...registrations, ...guestRegistrations].find(r => r.userId === user?.id)
  const userRegistered = !!myReg && myReg.status !== RegistrationStatus.Withdrawn
  const canSeeMessages = userRegistered || isNpoCoordinator() || isTeamMember()

  const retrieveScheduledActivityMessages = useCallback(async () => {
    if (!canSeeMessages) return // only get messages if user is registered, NPO coordinator, or a member (if Field Trip)
    if (scheduledActivity.id && (scheduledActivity.status === ActivityStatus.Published || scheduledActivity.status === ActivityStatus.Canceled)) {
      if(ouIdForTeamMessages) {
        return await Promise.all([
          FieldDayAPI.listScheduledActivityMessages(scheduledActivityId, messageAbortController.current, undefined, undefined),
          FieldDayAPI.listScheduledActivityMessages(scheduledActivityId, messageAbortController.current, undefined, ouIdForTeamMessages)
        ]).then((axiosResp) => {
          const [messages, teamMessages] = axiosResp
          const newScheduledActivityMessages = messages.data
          const respETag = messages.headers?.etag

          let newDelayInterval = defaultDelay
          if (respETag && respETag === messagesETag) {
            const newTimeSinceNewMessage = timeSinceNewMessage + (refreshInterval ?? 0)
            newDelayInterval = newDelay(newTimeSinceNewMessage)
            setRefreshInterval(newDelayInterval)
            setTimeSinceNewMessage(newTimeSinceNewMessage)
            setTeamMessages(teamMessages.data)
          } else {
            setMessages(newScheduledActivityMessages)
            setTeamMessages(teamMessages.data)
            setRefreshInterval(newDelayInterval)
            setTimeSinceNewMessage(0)
          }

          if (respETag) {
            setMessageETag(respETag)
          }
        })
      } else {
        FieldDayAPI.listScheduledActivityMessages(scheduledActivityId, messageAbortController.current, undefined)
        .then(axiosResp => {
          const newScheduledActivityMessages = axiosResp.data
          const respETag = axiosResp.headers?.etag

          let newDelayInterval = defaultDelay
          if (respETag && respETag === messagesETag) {
            const newTimeSinceNewMessage = timeSinceNewMessage + (refreshInterval ?? 0)
            newDelayInterval = newDelay(newTimeSinceNewMessage)
            setRefreshInterval(newDelayInterval)
            setTimeSinceNewMessage(newTimeSinceNewMessage)
          } else {
            setMessages(newScheduledActivityMessages)
            setRefreshInterval(newDelayInterval)
            setTimeSinceNewMessage(0)
          }

          if (respETag) {
            setMessageETag(respETag)
          }
        })
        .catch(e => {
          if (axios.isCancel(e)) {
            console.log(`Request canceled with message "${e.message}"`)
          } else {
            console.log(`Request error ${e}`)
            console.log(`Resetting refresh interval`)
            setRefreshInterval(5)
          }
        })
      }

    }
  }, [scheduledActivity.id, canSeeMessages, scheduledActivityMessages, scheduledActivityTeamMessages,refreshInterval, timeSinceNewMessage, newDelay])

  // Run once when the SA is loaded and registrations are loaded
  useEffect(() => {
    if (!canSeeMessages) return
    retrieveScheduledActivityMessages()

    return () => {
      // When the component is unmounted, cancel any
      // requests made with the messageAbortController
      console.log("Canceling all message retrieval requests on component unmount")
      messageAbortController.current.abort()
    }
  }, [canSeeMessages])

  // Run periodically based on refreshInterval.
  // If refreshInterval changes, the interval is recreated.
  // Set refreshInterval to null to disable the refresh.
  useInterval(() => {
    retrieveScheduledActivityMessages()
  }, refreshInterval)

  /**********************/
  /* EXPORTED FUNCTIONS */
  /**********************/

  const createScheduledActivityMessage = async (messageBody: string, parentMessageId?: string, orgUnitId?: string | null): Promise<boolean> => {
    const resp = await FieldDayAPI.createScheduledActivityMessage(scheduledActivityId, messageBody, parentMessageId, undefined, orgUnitId)
    if (!resp.success) {
      setAlert(AlertSeverity.ERROR, resp.errorMessage)
    }
    await retrieveScheduledActivityMessages()
    return resp.success
  }

  const pauseMessagesSubscription = () => {
    console.log(`Pausing messages subscription`)
    setRefreshInterval(null)
  }

  const resumeMessagesSubscription = () => {
    console.log(`Resuming messages subscription`)
    setRefreshInterval(defaultDelay)
  }

  return {
    scheduledActivityMessages,
    scheduledActivityTeamMessages,
    createScheduledActivityMessage,
    pauseMessagesSubscription,
    resumeMessagesSubscription,
    messagesLoaded: scheduledActivityMessages !== null,
    canSeeMessages,
  }
}

// Determine a new delay interval depending on the time since a new message was received.
// This is only exported for testing.
export function newDelay(timeSinceNewMessage: number): number {
  // Sort descending to be safe
  const messageTimesQuanta = [120, 90, 60, 30].sort((a, b) => b - a)

  for (const messageTimesQuantum of messageTimesQuanta) {
    if (timeSinceNewMessage >= messageTimesQuantum) {
      return messageTimesQuantum / 2
    }
  }

  return defaultDelay
}
